import React, { useState, useMemo, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import connect from '../../../utils/connect';
import {
    getPlacementOptions,
    getRHOptions,
    getPlatformOptions,
    getAccountOptions,
} from '../getSpecificOptions';
import getOptions from '../../../utils/getFilterOptions';
import { getGreaterThanOptions } from '../../../social-media-posts/dropdownOptions';
import getSelectOptions from '../../../utils/getSelectOptions';
import {
    PREMIUM_COLLECTIONS,
    PREMIUM_SMP_BELOW_THIRTY_PERCENT,
    PREMIUM_PLACEMENT_TYPES,
    LINKED_FILTER_LOGIC,
    INTERNATIONAL_BROADCAST_VIEW,
    GROUP_BY_BROADCAST_METADATA,
} from '../../../constants/config';
import { expandPlacementOptions } from '../../../utils/filtersHelperFunctions';

import { useFormikContext } from 'formik';
import getGroupByOptions from '../../../insights/getGroupByOptions';
import { parseSelectedRightsholderPlacements } from '../../../utils/parseSelectedPlacements';
import { getSyncedCompareDates } from '../insights/compareDateUtils';

export const FiltersFormContext = React.createContext(null);

const FiltersFormProvider = ({
    children,
    filters,
    client,
    broadcasts,
    isLoading,
    origin,
    isOverridingFilters = false,
}) => {
    const { t } = useTranslation('common');
    const { setFieldValue, values } = useFormikContext();
    const isSettingFilters = {
        rightsholders: false,
        sponsors: false,
        placements: false,
        placementTypes: false,
    };

    const setIsSettingFilters = (key, value) => () => ({
        ...isSettingFilters,
        [key]: value,
    });

    const [isPlatformSelectionDisabled, setIsPlatformSelectionDisabled] =
        useState(false);
    const [isCountrySelectionDisabled, setIsCountrySelectionDisabled] =
        useState(false);

    const hasPremiumCollections =
        client.permissions.includes(PREMIUM_COLLECTIONS);
    const isAllowingPostsMVPBelow30 = client.permissions.includes(
        PREMIUM_SMP_BELOW_THIRTY_PERCENT,
    );

    const hasPremiumPlacementTypes = client.permissions.includes(
        PREMIUM_PLACEMENT_TYPES,
    );

    const hasFilterLogic = client.permissions.includes(LINKED_FILTER_LOGIC);

    const hasInternationalBroadcastView = client.permissions.includes(
        INTERNATIONAL_BROADCAST_VIEW,
    );

    const hasGroupByBroadcastMetadata = client.permissions.includes(
        GROUP_BY_BROADCAST_METADATA,
    );

    const userPermissions = client.permissions || [];
    const {
        rightsholders,
        shouldShowLeagueHierarchy,
        sponsors,
        placements,
        hasSeveralRightsholders,
        platformPostTypePairs,
        accOwnTypes,
        mediaTypes,
        collections,
        placementTypes,
        validRsps,
        countries,
        broadcastNetworks,
    } = filters;

    const { names = [] } = broadcasts;

    const allRightsholdersOptions = useMemo(
        () =>
            getRHOptions({
                rhList: rightsholders.list,
                shouldShowLeagueHierarchy,
            }),
        [rightsholders, shouldShowLeagueHierarchy],
    );

    const allSponsorsOptions = useMemo(
        () =>
            getOptions({
                list: sponsors.list,
            }),
        [sponsors],
    );

    const allPlacementsOptions = useMemo(
        () =>
            getPlacementOptions({
                plcList: placements.list,
                rhList: rightsholders.list,
                hasSeveralRightsholders,
            }),
        [placements],
    );

    const platformsOptions = useMemo(
        () =>
            getPlatformOptions({
                list: platformPostTypePairs.list,
                nested: true,
            }),
        [platformPostTypePairs],
    );

    const accTypesOptions = useMemo(
        () =>
            getAccountOptions({
                list: accOwnTypes.list,
                userPermissions,
            }),
        [accOwnTypes],
    );

    const mediaTypesOptions = useMemo(() => {
        const opts = getOptions({
            list: mediaTypes.list,
        });

        return opts.filter(({ label }) => label !== 'N/A');
    }, [mediaTypes]);

    const collectionsOptions = useMemo(
        () =>
            getOptions({
                list: collections.list,
            }),
        [collections],
    );

    const allPlacementTypeOptions = useMemo(
        () =>
            getOptions({
                list: placementTypes.list,
            }),
        [placementTypes],
    );

    const translatedGreaterThanOptions = getGreaterThanOptions(t);
    const MVPoptions = isAllowingPostsMVPBelow30
        ? translatedGreaterThanOptions
        : translatedGreaterThanOptions.filter(({ value }) => value >= 0.3);

    const broadcastNameOptions = useMemo(
        () =>
            (Array.isArray(names) ? names : []).map((name, i) => ({
                value: i,
                label: name,
            })),
        [names],
    );

    const countryOptions = useMemo(
        () =>
            getOptions({
                list: countries?.list,
            }),
        [countries],
    );

    const broadcastNetworkOptions = useMemo(
        () =>
            getOptions({
                list: broadcastNetworks?.list,
            }),
        [broadcastNetworks],
    );

    const [rspFilteredOptions, setRspFilteredOptions] = useState([
        allRightsholdersOptions,
        allSponsorsOptions,
        allPlacementsOptions,
        allPlacementTypeOptions,
    ]);

    const [
        rhFilteredOptions,
        spFilteredOptions,
        plFilteredOptions,
        plTypeFilteredOptions,
    ] = rspFilteredOptions;

    // Generic Set Form Values
    const setFormValue = fieldName => value =>
        setFieldValue(fieldName, value, false);

    // Set RHs
    const setRightsholders = value => {
        setFieldValue('rightsholders', value, false);
    };

    const setRightsholdersLeaguePairs = value => {
        const newRightsholdersLeaguePairs =
            value?.reduce((acc, v) => {
                if (v?.startsWith('league_')) {
                    // eslint-disable-next-line @typescript-eslint/no-unused-vars
                    const [_, leagueId] = v.split('_');
                    const nestedRhs = rhFilteredOptions.find(({ value }) => {
                        const l =
                            leagueId !== 'null' ? parseInt(leagueId) : null;
                        return value === l;
                    })?.nested;

                    nestedRhs?.forEach(({ value }) => {
                        if (leagueId === 'null') {
                            const rh = rightsholders.list[value];

                            if (rh?.leagueId) {
                                acc[`${rh.leagueId}-${value}`] = true;
                            } else {
                                acc[`other-${value}`] = true;
                            }
                        } else {
                            acc[`${leagueId}-${value}`] = true;
                        }
                    });
                } else {
                    const rhId = v;

                    const rh = rightsholders.list[rhId];

                    const leagueId = rh?.leagueId;

                    if (leagueId) {
                        acc[`${leagueId}-${rhId}`] = true;
                    } else {
                        acc[`other-${rhId}`] = true;
                    }
                }
                return acc;
            }, {}) || {};

        setFieldValue(
            'rightsholdersLeaguePairs',
            newRightsholdersLeaguePairs,
            false,
        );

        const leaguesValues = value.filter(v => v.startsWith('league_'));

        const leagueRhs = leaguesValues.reduce((acc, v) => {
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const [_, l] = v.split('_');

            const leagueId = l !== 'null' ? parseInt(l) : null;

            const nestedRhs = rhFilteredOptions.find(
                ({ value }) => value === leagueId,
            )?.nested;

            if (nestedRhs) {
                acc.push(...nestedRhs.map(({ value }) => value));
            }

            return acc;
        }, []);

        const selectedRightsholders = value.filter(
            v => !v.startsWith('league_'),
        );

        const rhs = [...selectedRightsholders, ...leagueRhs];

        setRightsholders(rhs);
    };

    const setPlacements = value => {
        const selectedPlacements = parseSelectedRightsholderPlacements(
            value,
            placements.list,
        );
        setFieldValue('placements', selectedPlacements, false);
    };

    const setSelectedBroadcastNames = options => {
        const mediaUploadNames = broadcastNameOptions.reduce(
            (acc, { value, label }) => {
                if (options.includes(value)) {
                    acc.push(label);
                }
                return acc;
            },
            [],
        );

        setFieldValue('broadcastNames', mediaUploadNames, false);
        setFieldValue('broadcastOptions', options, false);
    };

    const [groupByOptions, setGroupByOptions] = useState([]);

    useEffect(() => {
        (async () => setGroupByOptions(await getGroupByOptions(t)))();
    }, [t]);

    useEffect(() => {
        const nextCompareDates = getSyncedCompareDates({
            startDate: values.startDate,
            endDate: values.endDate || new Date(),
            compareDates: values.compareDates,
        });

        if (nextCompareDates) {
            setFieldValue('compareDates', nextCompareDates, false);
        }
    }, [
        setFieldValue,
        values.startDate,
        values.endDate,
        values.compareDates?.option,
        values.compareDates?.dates?.startDate,
        values.compareDates?.dates?.endDate,
    ]);

    const getInsightsGroupByOptions = useCallback(() => {
        // Labels are already translated from getGroupByOptions
        let options = [...groupByOptions];

        // Inserts 'Collection' option into group by list, after Account Type
        // Only if Client has Premium Collections permission
        if (hasPremiumCollections) {
            const index =
                options.findIndex(el => {
                    return el.value === 'accountOwnershipType';
                }) + 1;
            options.splice(index, 0, {
                label: t('groupByOptions.collection'),
                value: 'collection',
            });
        }

        // Inserts 'Placement Type' option into group by list, after Placement
        // Only if Client has Premium Placement Types permission
        if (hasPremiumPlacementTypes) {
            const index =
                options.findIndex(el => {
                    return el.value === 'rightsholderPlacement';
                }) + 1;
            options.splice(index, 0, {
                label: t('groupByOptions.placementType'),
                value: 'placementType',
            });
        }

        if (hasInternationalBroadcastView) {
            options.push({
                label: t('groupByOptions.country'),
                value: 'country',
            });
        }

        if (hasGroupByBroadcastMetadata) {
            options.push({
                label: t('groupByOptions.seasonType'),
                value: 'seasonType',
            });
            options.push({
                label: t('groupByOptions.gameType'),
                value: 'gameType',
            });
            options.push({
                label: t('groupByOptions.homeTeam'),
                value: 'homeTeam',
            });
            options.push({
                label: t('groupByOptions.awayTeam'),
                value: 'awayTeam',
            });
        }

        if (values?.compareDates?.option) {
            options = options.filter(option => option.value !== 'date');
        }

        return options;
    }, [
        groupByOptions,
        hasPremiumCollections,
        hasPremiumPlacementTypes,
        hasInternationalBroadcastView,
        values?.compareDates?.option,
        t,
    ]);

    useEffect(() => {
        if (!hasFilterLogic) {
            setRspFilteredOptions([
                allRightsholdersOptions,
                allSponsorsOptions,
                allPlacementsOptions,
                allPlacementTypeOptions,
            ]);
            return;
        }
        const [
            rhFilteredOptions,
            spFilteredOptions,
            plFilteredOptions,
            plTypeFilteredOptions,
        ] = rspFilteredOptions;

        let valid = {
            rightsholders: new Set(),
            sponsors: new Set(),
            placements: new Set(),
            placementTypes: new Set(),
        };

        const previousValid = {
            rightsholders: getSelectOptions(rhFilteredOptions, false),
            sponsors: getSelectOptions(spFilteredOptions, false),
            placements: getSelectOptions(plFilteredOptions, true),
            placementTypes: getSelectOptions(plTypeFilteredOptions, false),
        };

        const selectedRsps = {
            rightsholders: values.rightsholders,
            sponsors: values.sponsors,
            placements: expandPlacementOptions(values.placements),
            placementTypes: values.placementTypes,
        };

        for (const rsp of validRsps) {
            const [r, s, p, plt] = rsp.split('_');

            const rspObj = {
                rightsholders: r,
                sponsors: s,
                placements: p,
                placementTypes: plt,
            };

            // Check if the current R-S-P combination matches the selected values
            Object.keys(rspObj).forEach(key => {
                if (
                    Object.entries(selectedRsps)
                        .filter(([k]) => key !== k)
                        .every(
                            ([k, sel]) =>
                                sel.length === 0 || sel.includes(rspObj[k]),
                        )
                ) {
                    valid[key].add(rspObj[key]);
                }
            });
        }

        const filteredRhList = Object.fromEntries(
            Object.entries(rightsholders.list).filter(([key]) => {
                return (
                    valid.rightsholders.has(key) ||
                    (isSettingFilters.rightsholders &&
                        (!previousValid.rightsholders.length ||
                            previousValid.rightsholders.includes(key)))
                );
            }),
        );

        const filteredPlList = Object.fromEntries(
            Object.entries(placements.list).filter(
                ([key]) =>
                    valid.placements.has(key) ||
                    (isSettingFilters.placements &&
                        (!previousValid.placements.length ||
                            previousValid.placements.includes(key))),
            ),
        );

        const filteredRhOptions = getRHOptions({
            rhList: filteredRhList,
            shouldShowLeagueHierarchy,
        });

        const filteredSpOptions = allSponsorsOptions.filter(
            ({ value }) =>
                valid.sponsors.has(value) ||
                (isSettingFilters.sponsors &&
                    (!previousValid.sponsors.length ||
                        previousValid.sponsors.includes(value))),
        );

        const filteredPlOptions = getPlacementOptions({
            plcList: filteredPlList,
            rhList: rightsholders.list,
            hasSeveralRightsholders,
        });

        const filteredPlTypeOptions = allPlacementTypeOptions.filter(
            ({ value }) =>
                valid.placementTypes.has(value) ||
                (isSettingFilters.placementTypes &&
                    (!previousValid.placementTypes.length ||
                        previousValid.placementTypes.includes(value))),
        );

        setRspFilteredOptions([
            filteredRhOptions,
            filteredSpOptions,
            filteredPlOptions,
            filteredPlTypeOptions,
        ]);
    }, [
        values.rightsholders,
        values.sponsors,
        values.placements,
        values.placementTypes,
        allRightsholdersOptions,
        allSponsorsOptions,
        allPlacementsOptions,
        allPlacementTypeOptions,
    ]);

    const providerValue = {
        rightsholdersOptions: rhFilteredOptions,
        sponsorsOptions: spFilteredOptions,
        placementsOptions: plFilteredOptions,
        setFormValue,
        setRightsholders,
        setRightsholdersLeaguePairs,
        setPlacements,
        hasPremiumCollections,
        hasPremiumPlacementTypes,
        platformsOptions,
        accTypesOptions,
        mediaTypesOptions,
        collectionsOptions,
        MVPoptions,
        broadcastNameOptions,
        setSelectedBroadcastNames,
        getInsightsGroupByOptions,
        placementTypeOptions: plTypeFilteredOptions,
        setIsSettingFilters,
        isLoading,
        origin,
        hasInternationalBroadcastView,
        countryOptions,
        broadcastNetworkOptions,
        isPlatformSelectionDisabled,
        setIsPlatformSelectionDisabled,
        isCountrySelectionDisabled,
        setIsCountrySelectionDisabled,
        isOverridingFilters,
    };

    return (
        <FiltersFormContext.Provider value={providerValue}>
            {children}
        </FiltersFormContext.Provider>
    );
};

FiltersFormProvider.propTypes = {
    children: PropTypes.node.isRequired,
    filters: PropTypes.object.isRequired,
    client: PropTypes.object.isRequired,
    broadcasts: PropTypes.object.isRequired,
    isLoading: PropTypes.bool,
    origin: PropTypes.string,
    isOverridingFilters: PropTypes.bool,
};

export default connect(['filters', 'client', 'broadcasts'])(
    FiltersFormProvider,
);
